import type { flruCache } from 'flru';

const loadError = new Error('Failed to load image and fallback');

export function asyncDrawImage(options: {
  src: string;
  fallback?: string;
  timeout?: number;
  mediaCache?: flruCache<HTMLElement | null>;
  crossOrigin?: string | null;
  referrerPolicy?: ReferrerPolicy | null;
  cacheKeyPrefix?: string;
}): Promise<HTMLImageElement> {
  const {
    src,
    fallback,
    timeout = 10000,
    mediaCache,
    crossOrigin = 'Anonymous',
    referrerPolicy,
    cacheKeyPrefix,
  } = options;

  const cacheKey = cacheKeyPrefix ? `${cacheKeyPrefix}:${src}` : src;

  return new Promise((resolve, reject) => {
    if (mediaCache?.has(cacheKey)) {
      const cacheImg = mediaCache.get(cacheKey);

      if (cacheImg instanceof HTMLImageElement) {
        resolve(cacheImg);

        return;
      }

      if (cacheImg === null) {
        reject(loadError);

        return;
      }
    }

    const cacheResolve = (img: HTMLImageElement) => {
      mediaCache?.set(cacheKey, img);
      resolve(img);
    };

    const cacheReject = (error: Error) => {
      mediaCache?.set(cacheKey, null);
      reject(error);
    };

    const processFallback = () => {
      if (fallback) {
        // fallback 图片使用原始URL作为缓存key（不带前缀），避免污染主缓存
        asyncDrawImage({
          src: fallback,
          timeout,
          mediaCache,
        })
          .then(cacheResolve)
          .catch(cacheReject);
      } else {
        // 如果没有 fallback 或者 fallback 也失败，返回错误
        cacheReject(loadError);
      }
    };
    const onerror = () => {
      if (crossOrigin) {
        // 第二次加载：去掉 crossOrigin 并设置 referrerPolicy='no-referrer'
        // 这样请求不携带 Origin 和 Referer，可绕过部分防盗链 CDN（如小红书）
        // 代价：图片以 tainted 模式加载，canvas toDataURL 不可用
        asyncDrawImage({
          src,
          timeout,
          mediaCache,
          crossOrigin: null,
          referrerPolicy: 'no-referrer',
          cacheKeyPrefix,
        })
          .then(cacheResolve)
          .catch(processFallback);
      } else {
        processFallback();
      }
    };

    const img = new Image();

    // crossOrigin 和 referrerPolicy 必须在 src 之前设置，否则浏览器会忽略
    if (crossOrigin) {
      img.crossOrigin = crossOrigin;
    }

    if (referrerPolicy) {
      img.referrerPolicy = referrerPolicy;
    }

    img.src = src;

    // 设置超时
    const timeoutId = setTimeout(onerror, timeout);

    img.onload = () => {
      clearTimeout(timeoutId);
      cacheResolve(img);
    };

    img.onerror = () => {
      clearTimeout(timeoutId);
      onerror();
    };

    img.onabort = () => {
      clearTimeout(timeoutId);
      onerror();
    };
  });
}

/**
 * 计算图片最佳缩放尺寸
 * @param {number} containerWidth 容器宽度
 * @param {number} containerHeight 容器高度
 * @param {number} naturalWidth 图片原始宽度
 * @param {number} naturalHeight 图片原始高度
 * @returns {{ width: number, height: number }}
 */
export function calculateImageSize(
  containerWidth: number,
  containerHeight: number,
  naturalWidth: number,
  naturalHeight: number,
): { width: number; height: number } {
  if (containerWidth <= 0 || containerHeight <= 0) {
    return {
      width: 0,
      height: 0,
    };
  }

  if (naturalWidth <= 0 || naturalHeight <= 0) {
    return {
      width: containerWidth,
      height: containerHeight,
    };
  }

  // 计算宽高比例限制
  const widthRatio = containerWidth / naturalWidth;
  const heightRatio = containerHeight / naturalHeight;

  // 取最小值防止溢出
  const scale = Math.min(widthRatio, heightRatio);

  // 返回整数尺寸 (避免亚像素模糊)
  return {
    width: Math.floor(naturalWidth * scale),
    height: Math.floor(naturalHeight * scale),
  };
}

export async function getPreparedText(
  prepareText?: (text: string) => Promise<string>,
  text: string = '',
) {
  try {
    if (prepareText) {
      text = (await prepareText(text)) || text;
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`fail to prepareText`, e);
  }

  return text;
}
